import type {
  Envelope,
  SimulationRoomSegment,
} from "../routes/simulationRoomTypes"

/**
 * Folds one transcript.segment envelope into the segment map.
 *
 * Replay is addressed to a listener after live messages that carry higher
 * sequence numbers, so dedupe per segment rather than against a single
 * high-water mark — that would discard the whole replay.
 *
 * The simulator also re-emits a persona segment with a corrected end offset
 * once its audio finishes playing. The highest seq wins on content, while
 * `orderSeq` keeps the segment at the position of its first emission.
 */
export function mergeTranscriptSegment(
  current: Map<string, SimulationRoomSegment>,
  envelope: Envelope,
): Map<string, SimulationRoomSegment> {
  const payload = envelope.payload as {
    segment_id: string
    speaker: string
    text: string
    is_final: boolean
    start_offset_seconds?: number
    end_offset_seconds?: number
  }
  const existing = current.get(payload.segment_id)
  if (existing && existing.seq >= envelope.seq) {
    if (envelope.seq >= existing.orderSeq) {
      return current
    }
    // A replay of the segment's first emission arriving after a live
    // revision: the content is stale but the position is the original one,
    // so take the earlier order without touching the newer content.
    const reordered = new Map(current)
    reordered.set(payload.segment_id, { ...existing, orderSeq: envelope.seq })
    return reordered
  }
  const next = new Map(current)
  next.set(payload.segment_id, {
    segmentId: payload.segment_id,
    seq: envelope.seq,
    orderSeq: Math.min(existing?.orderSeq ?? envelope.seq, envelope.seq),
    speaker: payload.speaker,
    text: payload.text,
    isFinal: payload.is_final,
    // A revision may carry only the bound it corrects; the other one is
    // inherited from the revision it replaces rather than forgotten.
    startOffsetSeconds:
      payload.start_offset_seconds ?? existing?.startOffsetSeconds,
    endOffsetSeconds: payload.end_offset_seconds ?? existing?.endOffsetSeconds,
  })
  return next
}
